import { useEffect, useMemo, useState } from 'react';
import type { RawInputs } from './types';
import { validateInputs } from './calculator/validation';
import {
  DEFAULT_Q10_SENSITIVITY_VALUES,
  calculate,
  calculateQ10Sensitivity,
  calculateTemperatureScenarios,
  defaultTemperatureSweep,
} from './calculator/acceleratedAging';
import { allVerificationCases } from './data/verificationCases';
import { runCases, summarise } from './calculator/verification';
import { APP_META, DISCLAIMER_SHORT } from './data/provenance';
import { Disclosure, Section, ValidationMessages } from './components/Primitives';
import { InputPanel } from './components/InputPanel';
import { ResultPanel } from './components/ResultPanel';
import { DerivationPanel } from './components/DerivationPanel';
import { Q10Sensitivity } from './components/Q10Sensitivity';
import { TemperatureExplorer } from './components/TemperatureExplorer';
import { ConsiderationsPanel } from './components/ConsiderationsPanel';
import { VerificationCenter } from './components/VerificationCenter';
import { SummaryPanel } from './components/SummaryPanel';
import { DisclaimerBlock, ProvenancePanel, ReferencesPanel } from './components/TechnicalBasis';
import { trackEvent } from './utils/analytics';

/**
 * Opening values exist so the tool demonstrates itself on load. They are a
 * worked example, not a recommendation, and the interface says so wherever
 * they appear.
 */
const INITIAL_INPUTS: RawInputs = {
  shelfLifeValue: '3',
  shelfLifeUnit: 'years',
  referenceTempC: '25',
  acceleratedTempC: '55',
  q10: '2',
  startDate: '',
};

const EMPTY_INPUTS: RawInputs = {
  shelfLifeValue: '',
  shelfLifeUnit: 'years',
  referenceTempC: '',
  acceleratedTempC: '',
  q10: '',
  startDate: '',
};

export default function App() {
  const [inputs, setInputs] = useState<RawInputs>(INITIAL_INPUTS);
  const [q10Values, setQ10Values] = useState<number[]>([...DEFAULT_Q10_SENSITIVITY_VALUES]);
  const [tempStep, setTempStep] = useState(5);
  const [checked, setChecked] = useState<string[]>([]);

  const validation = useMemo(() => validateInputs(inputs), [inputs]);
  const parsed = validation.parsed;

  const result = useMemo(() => (parsed ? calculate(parsed) : null), [parsed]);

  const sensitivity = useMemo(
    () => (parsed ? calculateQ10Sensitivity(parsed, q10Values) : []),
    [parsed, q10Values],
  );

  const temperatureScenarios = useMemo(
    () => (parsed ? calculateTemperatureScenarios(parsed, defaultTemperatureSweep(parsed, tempStep)) : []),
    [parsed, tempStep],
  );

  const verification = useMemo(() => summarise(runCases(allVerificationCases())), []);

  useEffect(() => {
    if (result) trackEvent('calculation_completed');
  }, [result]);

  const patch = (p: Partial<RawInputs>) => setInputs((prev) => ({ ...prev, ...p }));

  const toggleChecked = (id: string) => {
    trackEvent('checklist_item_toggled');
    setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="app">
      <header className="masthead">
        <div className="masthead-inner">
          <div className="masthead-title">
            <h1>
              Accelerated Aging <em>Explorer</em>
            </h1>
            <p className="masthead-tagline">{APP_META.tagline}</p>
          </div>
          <div className="masthead-meta">
            <span className="version-pill">{APP_META.version}</span>
            <span className="version-note">{verification.headline}</span>
          </div>
        </div>
        <div className="masthead-scope">
          <p>{DISCLAIMER_SHORT}</p>
          <a href="#scope">Full scope boundary</a>
        </div>
      </header>

      <main>
        <Section
          id="calculate"
          index="01"
          title="Calculate"
          purpose="Enter the scenario. Every value here is yours; none of them is suggested by the tool."
        >
          <ValidationMessages issues={validation.issues} />
          <div className="two-column">
            <InputPanel
              inputs={inputs}
              issues={validation.issues}
              onChange={patch}
              onReset={() => setInputs(EMPTY_INPUTS)}
            />
            <ResultPanel result={result} />
          </div>
        </Section>

        <Section
          id="explain"
          index="02"
          title="Explain"
          purpose="Where the number came from, step by step, with nothing rounded on the way."
        >
          <Disclosure summary="Show calculation" onOpen={() => trackEvent('derivation_viewed')} defaultOpen>
            <DerivationPanel result={result} />
          </Disclosure>
        </Section>

        <Section
          id="explore"
          index="03"
          title="Explore"
          purpose="How much of your answer is carried by a single assumed number."
        >
          <div className="panel">
            <h3 className="panel-title">Q10 sensitivity</h3>
            <div onFocus={() => trackEvent('sensitivity_viewed')}>
              <Q10Sensitivity rows={sensitivity} q10Values={q10Values} onQ10ValuesChange={setQ10Values} />
            </div>
          </div>
          <div className="panel">
            <h3 className="panel-title">Temperature scenarios</h3>
            <div onFocus={() => trackEvent('temperature_scenarios_viewed')}>
              <TemperatureExplorer rows={temperatureScenarios} step={tempStep} onStepChange={setTempStep} />
            </div>
          </div>
        </Section>

        <Section
          id="consider"
          index="04"
          title="Consider"
          purpose="What the arithmetic cannot see. These stay open questions for you to answer."
        >
          <div className="panel">
            <ConsiderationsPanel checked={checked} onToggle={toggleChecked} />
          </div>
        </Section>

        <Section
          id="verify"
          index="05"
          title="Verify"
          purpose="Whether this calculator's own arithmetic has been tested, and by whom."
        >
          <div className="panel" onFocus={() => trackEvent('verification_viewed')}>
            <VerificationCenter />
          </div>
        </Section>

        <Section
          id="document"
          index="06"
          title="Document"
          purpose="A record of the inputs, the derivation, the sensitivity, and the tool's limits."
        >
          <SummaryPanel
            result={result}
            sensitivity={sensitivity}
            temperatureScenarios={temperatureScenarios}
            checked={checked}
            verificationHeadline={verification.headline}
            developmentPassed={verification.developmentPassed}
            developmentTotal={verification.developmentTotal}
          />
        </Section>

        <Section
          id="basis"
          index="07"
          title="Technical basis"
          purpose="What this tool is built on, and what was actually examined."
        >
          <div className="panel" onFocus={() => trackEvent('references_viewed')}>
            <ReferencesPanel />
          </div>
          <div className="panel panel-quiet">
            <ProvenancePanel />
          </div>
        </Section>

        <DisclaimerBlock />
      </main>

      <footer className="colophon">
        <p>
          {APP_META.name} · {APP_META.version} · {APP_META.buildScopeNote}
        </p>
        <p>
          Calculations run entirely in your browser. Nothing you enter is transmitted, stored, or logged.
        </p>
      </footer>
    </div>
  );
}
